import { Box, Button, Drawer, Typography, Tab, useMediaQuery } from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { IFiltersValues } from '@diana/core';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import { useTheme } from '@mui/system';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import BunkeringOverview from './BunkeringOverview';
import { RawDataGrid } from '../DataGrids/RawDataGrid';
import { AlarmsDataGrid } from '../DataGrids/AlarmsDataGrid';
import { ExportButton } from '../Buttons/ExportButton';
import { useTranslation } from 'react-i18next';
import { RIGHT_DRAWER_NS } from './i18n';
import { FilterType } from '../../stores/useFilters';
import { HubsNames, useSignalRHubStore } from '../../stores/useSignalR';
import { useMessageStore } from '../../stores/useMessageStore';
import { convertFiltersToQuery } from '../../utils/convertFiltersToQuery';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { LIVE_SESSION_NS } from '../BunkeringDashboard/i18n';
import { useNavigate, useLocation } from 'react-router-dom';
import { MPAStatusDataGrid } from '../DataGrids/MpaStatusDataGrid';
import { StatusModal } from '../Modals/StatusModal';
import { useExportHandler } from '../../hooks/useExportHandler';
import { getRawDataExportFileName } from '../../utils/stringUtils';
import { exportData } from '../../utils/exportData';
import { useExportModal } from './hooks/useExportModal';
import { BunkeringData } from './types';
import { isEmpty } from 'lodash';

interface RightDrawerProps {
  selectedBMT: string | number;
  isDrawerOpened: boolean;
  setIsDrawerOpened: (value: boolean) => void;
  data: BunkeringData | Record<string, never>;
}

export default function RightDrawer(props: RightDrawerProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const rawDataGridRef = useRef<GridApiPremium>(null);
  const alarmsDataGridRef = useRef<GridApiPremium>(null);
  const MPAStatusDataGridRef = useRef<GridApiPremium>(null);
  const { t } = useTranslation([RIGHT_DRAWER_NS, LIVE_SESSION_NS]);
  const { selectedBMT, isDrawerOpened, setIsDrawerOpened, data } = props;
  const [value, setValue] = useState('1');
  const [isDrawerFullscreen, setIsDrawerFullscreen] = useState(false);
  const params = useParams();
  const { breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.up(700));
  const { setStreamArgs, setNonStreamArgs } = useSignalRHubStore((state) => state);
  const isBunkeringData = useMemo(() => {
    return 'deviceInfo' in data && 'bunkerActivePeriod' in data;
  }, [data]);

  const filtersValues = useMemo(() => {
    if (!isBunkeringData) {
      return {} as IFiltersValues;
    }

    const bmtFilter = {
      [FilterType.BMT_NUMBER]: [
        {
          value: selectedBMT,
          displayName: selectedBMT,
          deviceId: data.deviceInfo.id,
        },
      ],
    };

    if (!selectedBMT) {
      return {
        ...bmtFilter,
        [FilterType.TIME_RANGE]: [
          {
            value: data.bunkerActivePeriod.start.date,
            displayName: data.bunkerActivePeriod.start.date,
          },
          ...(data.bunkerActivePeriod.end
            ? [
                {
                  value: data.bunkerActivePeriod.end.date,
                  displayName: data.bunkerActivePeriod.end.date,
                },
              ]
            : []),
        ],
      };
    }

    return bmtFilter;
  }, [data, selectedBMT, isBunkeringData]);

  const { messages: bunkeringRawData = [], loading: stableLoading } = useMessageStore((state) => ({
    messages: state.messages[HubsNames.bunkeringRawData],
    loading: state.stableLoadingFlags[HubsNames.bunkeringRawData],
  }));

  const { messages: alarmLogsData = [], loading: alarmLogsLoading } = useMessageStore((state) => ({
    messages: state.messages[HubsNames.alarmsOverview],
    loading: state.stableLoadingFlags[HubsNames.alarmsOverview],
  }));

  const { messages: MPAStatusData = [], loading: MPAStatusLoading } = useMessageStore((state) => ({
    messages: state.messages[HubsNames.mpaStatusLogs],
    loading: state.stableLoadingFlags[HubsNames.mpaStatusLogs],
  }));

  const { loading: isLoadingDeliveryProfile } = useMessageStore((state) => ({
    loading: state.stableLoadingFlags[HubsNames.bunkeringOverview],
  }));

  useEffect(() => {
    if (isBunkeringData && selectedBMT !== 0) {
      setStreamArgs(HubsNames.bunkeringRawData, convertFiltersToQuery(filtersValues, null));
      setStreamArgs(HubsNames.bunkeringOverview, convertFiltersToQuery(filtersValues));
      setStreamArgs(HubsNames.aggregateOverview, convertFiltersToQuery(filtersValues));
      setStreamArgs(HubsNames.alarmsOverview, convertFiltersToQuery(filtersValues, null));
      setStreamArgs(HubsNames.mpaStatusLogs, convertFiltersToQuery(filtersValues, null));
      setNonStreamArgs(HubsNames.mpaStatusSubmit, convertFiltersToQuery(filtersValues, null));

      const searchParams = location.search;
      const [, rootPath] = location.pathname.split('/');
      const subPath = selectedBMT ? '/' + selectedBMT : '';
      navigate(`/${rootPath}${subPath}${searchParams}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBunkeringData, selectedBMT]);

  // Separate effect for handling drawer state and URL
  useEffect(() => {
    if (!isDrawerOpened && selectedBMT !== 0) {
      const [, rootPath] = location.pathname.split('/');
      navigate(`/${rootPath}${location.search}`, { replace: true });
    }
  }, [isDrawerOpened, selectedBMT, location.pathname, location.search, navigate]);

  useEffect(() => {
    setValue(
      params.drawerTab === 'raw'
        ? '2'
        : params.drawerTab === 'alarm'
          ? '3'
          : params.drawerTab === 'transmission'
            ? '4'
            : '1',
    );
  }, [params.drawerTab]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    const { origin, pathname, search } = window.location;
    const [, link, deviceIdBmt] = pathname.split('/');

    const getPathWithSearch = (suffix?: string) => {
      const suffixPath = suffix ? `/${suffix}` : '';
      return `${origin}/${link}/${deviceIdBmt}${suffixPath}${search}`;
    };

    const paths: Record<string, string> = {
      '1': getPathWithSearch(),
      '2': getPathWithSearch('raw'),
      '3': getPathWithSearch('alarm'),
      '4': getPathWithSearch('transmission'),
    };

    if (paths[newValue]) {
      window.history.pushState({}, '', paths[newValue]);
    }

    setValue(newValue);
  };

  const { palette } = useTheme();

  const { handleExportConfirm } = useExportHandler({
    onExport: async (fileName: string) => {
      exportData(bunkeringRawData, fileName, 'data');
    },
    getFileName: () => getRawDataExportFileName(selectedBMT),
  });

  const { modalState, handleExport, handleCloseModal } = useExportModal(
    stableLoading || isLoadingDeliveryProfile,
    bunkeringRawData,
    selectedBMT,
    handleExportConfirm,
  );

  if (isEmpty(data)) {
    return null;
  }

  return (
    <Box>
      <Drawer
        sx={{
          '& .MuiDrawer-paper': {
            width: isDrawerFullscreen ? '100%' : isMobile ? '75%' : '100%',
            boxSizing: 'border-box',
            height: isMobile ? '100vh' : '93vh',
            marginTop: isMobile ? '0' : '7vh',
          },
        }}
        variant="persistent"
        anchor="right"
        open={isDrawerOpened}
      >
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', pt: '12px', paddingX: '16px' }}
        >
          <Box sx={{ display: 'flex' }}>
            <Box
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                setIsDrawerOpened(false);
                setIsDrawerFullscreen(false);
              }}
            >
              <CloseIcon fontSize="small" />
            </Box>
            <Typography sx={{ fontWeight: 500, ml: 1 }}>BMT: {selectedBMT}</Typography>
          </Box>
          <Box sx={{ display: 'flex' }}>
            <ExportButton isLoading={false} onHandleExportData={handleExport} />
            {isMobile ? (
              <Button
                sx={{
                  ml: '16px',
                  borderColor: palette.secondary.border,
                  bgColor: palette.secondary.main,
                  color: palette.text.primary,
                  maxHeight: '36px',
                }}
                variant="outlined"
                color="secondary"
                startIcon={isDrawerFullscreen ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
                onClick={() => setIsDrawerFullscreen((prevCheck) => !prevCheck)}
                disabled={false}
              >
                {isDrawerFullscreen
                  ? t('components/RightDrawer:exitFullScreen')
                  : t('components/RightDrawer:fullScreen')}
              </Button>
            ) : null}
          </Box>
        </Box>
        <TabContext value={value}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList onChange={handleChange}>
              <Tab label={t('components/RightDrawer:tabs.bunkeringOverview')} value="1" />
              <Tab label={t('components/RightDrawer:tabs.transmissionLogs')} value="4" />
              <Tab label={t('components/RightDrawer:tabs.rawData')} value="2" />
              <Tab label={t('components/RightDrawer:tabs.alarmLogs')} value="3" />
            </TabList>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <TabPanel value="1">{value === '1' && <BunkeringOverview data={data} />} </TabPanel>
            <TabPanel value="2">
              {value === '2' && (
                <RawDataGrid ref={rawDataGridRef} data={bunkeringRawData} loading={stableLoading} />
              )}
            </TabPanel>
            <TabPanel value="3">
              {value === '3' && (
                <AlarmsDataGrid
                  ref={alarmsDataGridRef}
                  data={alarmLogsData}
                  loading={alarmLogsLoading}
                  hideBMTColumn={true}
                />
              )}
            </TabPanel>
            <TabPanel value="4">
              <MPAStatusDataGrid
                ref={MPAStatusDataGridRef}
                data={MPAStatusData.length ? MPAStatusData[0].details : []}
                loading={MPAStatusLoading}
              />
            </TabPanel>
          </Box>
        </TabContext>
      </Drawer>
      <StatusModal {...modalState} onClose={handleCloseModal} />
    </Box>
  );
}
